import styled, { css } from "styled-components";
import { Trans } from "@coworker/locales";
import { PriceBadge } from "@coworker/reusable/components/PriceBadge";
import { CopyToClipboardButton } from "../../components/RoomSettings/Atoms/CopyToClipboardButton";
import { StockAndInDeliveryInfo } from "@coworker/apprestructured/src/scan-and-search/components/product-card/StockAndIndeliveryInfo";
import { ItemExpectedDeliveryResponse } from "@coworker/apprestructured/src/shared/types/itemExpectedDeliveryResponse";

export const ProductInfo = styled("div")`
  padding: 0px 24px 16px 24px;
  border-bottom: 8px solid var(--grey150);
`;
export const DistancedRow = styled("div")`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  height: 100%;
`;
export const Title = styled("h1")`
  height: 24px;
  font-size: 18px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.33;
  letter-spacing: -0.5px;
  color: var(--grey900);
`;
export const Subtitle = styled("h2")`
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: var(--grey700);
`;

export const NumberBadge = styled("div")`
  display: inline-box;
  padding: 3px 10px;
  background-color: var(--black);
  font-size: 12px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: var(--white);
  max-width: 90px;
  box-sizing: border-box;
`;

const PriceBanner = styled("div")<{ family?: boolean; newLower?: boolean }>`
  font-size: 12px;
  font-weight: bold;
  line-height: 1.33;
  margin-bottom: 10px;
  ${({ family }) =>
    family &&
    css`
      color: var(--blue-family);
    `}
  ${({ newLower }) =>
    newLower &&
    css`
      color: var(--error);
    `}
`;

export const InformationWrapper = styled("div")`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PriceBadgesWrapper = styled("div")`
  display: flex;
  flex-direction: column;
`;

export const ProductIdAndPriceWrapper = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const NumberWrapper = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

interface ProductShowcaseProps {
  name: string;
  price: number;
  currency: string;
  priceUnit: string | undefined;
  originalPrice: number | undefined;
  isFamilyPrice: boolean;
  isBreathTakingItem: boolean;
  newLowerPrice: boolean;
  description: string;
  formattedNumber: string;
  available: number;
  elevated: number | null;
  color: string | undefined;
  isStockLoading: boolean;
  itemDeliveryData: ItemExpectedDeliveryResponse | undefined;
  isDeliveryDataLoading: boolean;
}

export function ProductShowcase({
  name,
  price,
  currency,
  priceUnit,
  originalPrice,
  isFamilyPrice,
  isBreathTakingItem,
  newLowerPrice,
  description,
  formattedNumber,
  available,
  elevated,
  color,
  isStockLoading,
  itemDeliveryData,
  isDeliveryDataLoading,
}: ProductShowcaseProps) {
  return (
    <ProductInfo>
      <ProductIdAndPriceWrapper>
        <NumberWrapper>
          <NumberBadge>{formattedNumber}</NumberBadge>
          <CopyToClipboardButton
            valueForClipboard={formattedNumber.replace(/\./g, "")}
            onClick={() => {}}
          />
        </NumberWrapper>
        <PriceBadgesWrapper>
          <PriceBadge
            price={price}
            currency={currency}
            priceUnit={priceUnit}
            isBreathTakingItem={isBreathTakingItem}
            originalPrice={originalPrice}
          />
        </PriceBadgesWrapper>
      </ProductIdAndPriceWrapper>
      {newLowerPrice && (
        <PriceBanner newLower>
          <Trans>lowPricString</Trans>
        </PriceBanner>
      )}
      {isFamilyPrice && (
        <PriceBanner family>
          <Trans>ikeaPriceString</Trans>
        </PriceBanner>
      )}
      <DistancedRow>
        <InformationWrapper>
          <div>
            <Title>{name}</Title>
            <Subtitle>{description}</Subtitle>
          </div>
        </InformationWrapper>
      </DistancedRow>
      <StockAndInDeliveryInfo
        available={available}
        elevated={elevated}
        color={color}
        isStockLoading={isStockLoading}
        itemDeliveryData={itemDeliveryData}
        isDeliveryDataLoading={isDeliveryDataLoading}
      />
    </ProductInfo>
  );
}
